import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material';

import { useSalesforceChatEnabled } from 'common/hooks/useSalesforceChat';
import { CILogger } from 'common/log/log.provider';

export const SupportChatTab = (): ReactElement | null => {
    const { t } = useTranslation('global', { useSuspense: false });
    const chatEnabled = useSalesforceChatEnabled();
    const [chatReady, setChatReady] = useState(false);
    const [isLaunching, setIsLaunching] = useState(false);

    useEffect(() => {
        if (!chatEnabled) {
            setChatReady(false);
            return;
        }

        // Guard against race: if the SF bootstrap already fired before this effect
        // registered its listener, utilAPI will already be present.
        if (window.embeddedservice_bootstrap?.utilAPI) {
            setChatReady(true);
            return;
        }

        const handleButtonCreated = () => setChatReady(true);
        window.addEventListener('onEmbeddedMessagingButtonCreated', handleButtonCreated);
        return () => window.removeEventListener('onEmbeddedMessagingButtonCreated', handleButtonCreated);
    }, [chatEnabled]);

    if (!chatEnabled || !chatReady) {
        return null;
    }

    const handleClick = () => {
        if (isLaunching) {
            return;
        }

        const launchPromise = window.embeddedservice_bootstrap?.utilAPI?.launchChat?.();
        if (launchPromise === undefined) {
            return;
        }

        setIsLaunching(true);
        launchPromise
            .catch((err: unknown) => {
                CILogger.getInstance().error('Salesforce chat failed to launch', err);
            })
            .finally(() => setIsLaunching(false));
    };

    return (
        <SupportButton
            type="button"
            data-testid="support-chat-button"
            onClick={handleClick}
            aria-disabled={isLaunching}
            aria-busy={isLaunching}
        >
            <span className="label">{isLaunching ? t('support_chat_tab_loading') : t('support_chat_tab')}</span>
            <span className="chevron" aria-hidden="true">
                ^
            </span>
        </SupportButton>
    );
};

const SupportButton = styled('button', { name: 'SupportChatTab' })(({ theme }) => ({
    position: 'fixed',
    insetBlockEnd: '0',
    insetInlineEnd: '104px',
    minWidth: '6rem',
    width: 'auto',
    height: '27px',
    borderRadius: '4px 4px 0 0',
    border: 'none',
    backgroundColor: theme.ciPalette.text.lightBlack,
    color: theme.ciPalette.common.white,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: 1,
    textAlign: 'start',
    padding: '4px 8px',
    zIndex: 1400,
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.08)',

    '& .label': {
        display: 'inline-block',
        marginTop: '1px',
        color: 'inherit',
    },

    '& .chevron': {
        fontSize: theme.typography.body2.fontSize,
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1,
        color: 'inherit',
    },

    '&:hover:not([aria-disabled="true"])': {
        backgroundColor: '#2f2f2f',
    },

    '&:active:not([aria-disabled="true"])': {
        backgroundColor: '#1a1a1a',
    },

    '&:focus-visible': {
        outline: `2px solid ${theme.ciPalette.common.blue}`,
        outlineOffset: '2px',
    },

    '&[aria-disabled="true"]': {
        opacity: 0.85,
        cursor: 'default',
    },
}));
